import { useEffect, useState } from "react";

/**
 * Drop-in replacement for multiple useState filter declarations.
 * Persists all filter values to sessionStorage under `storageKey`.
 * On mount, restores saved values; falls back to `defaults` if nothing saved.
 *
 * @param {string} storageKey  - unique key per page, e.g. "ctr_filters"
 * @param {object} defaults    - default values when nothing is saved
 * @returns [filters, setFilter]
 *   filters     - object with current filter values
 *   setFilter   - (key, value) => void  OR  (updaterFn) => void
 */
const usePersistedFilters = (storageKey, defaults, overrideKeys) => {
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey));
      if (saved && typeof saved === "object") {
        // Merge: saved values take priority, fall back to defaults for any missing keys
        // BUT if overrideKeys are provided (e.g. from navigation state), they always win
        const merged = { ...defaults, ...saved };
        if (overrideKeys && typeof overrideKeys === "object") {
          Object.keys(overrideKeys).forEach((k) => {
            if (overrideKeys[k] !== undefined && overrideKeys[k] !== null) {
              merged[k] = overrideKeys[k];
            }
          });
          // Persist immediately so a re-mount before useEffect fires doesn't read stale values
          sessionStorage.setItem(storageKey, JSON.stringify(merged));
        }
        return merged;
      }
    } catch {
      // ignore parse errors
    }
    return { ...defaults };
  });

  // Persist to sessionStorage whenever filters change
  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [storageKey, filters]);

  /**
   * setFilter("searchTerm", "lokesh")          — set one key
   * setFilter(prev => ({ ...prev, sortBy: "ACTIVE" }))  — updater fn
   */
  const setFilter = (keyOrUpdater, value) => {
    if (typeof keyOrUpdater === "function") {
      setFilters(keyOrUpdater);
    } else {
      setFilters((prev) => ({ ...prev, [keyOrUpdater]: value }));
    }
  };

  return [filters, setFilter];
};

export default usePersistedFilters;
